import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Theme, ThemeKey, ThemeMeta } from '../themes/types';
import { defaultTheme } from '../themes/theme-default';

interface ThemeContextType {
  theme: Theme;
  setTheme: (themeKey: ThemeKey) => Promise<void>;
  availableThemes: ThemeKey[];
  availableThemesMeta: ThemeMeta[];
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = 'app-theme-key';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [availableThemes, setAvailableThemes] = useState<ThemeKey[]>(['default']);
  const [availableThemesMeta, setAvailableThemesMeta] = useState<ThemeMeta[]>([]);

  const injectCSSVariables = useCallback((themeConfig: Theme) => {
    const root = document.documentElement;

    // 注入颜色变量（简化命名，移除 color-前缀）
    Object.entries(themeConfig.colors).forEach(([key, value]) => {
      const varName = key.startsWith('bg-') || key.startsWith('accent-') || key.startsWith('text-') || key.startsWith('border-')
        ? `--${key}`
        : `--color-${key}`;
      root.style.setProperty(varName, value);
    });

    // 注入图片变量
    Object.entries(themeConfig.images).forEach(([key, value]) => {
      if (value === null) {
        root.style.setProperty(`--image-${key}`, 'none');
      } else if (typeof value === 'string') {
        root.style.setProperty(`--image-${key}`, `url(${value})`);
      } else if (typeof value === 'object' && value !== null) {
        // 处理复杂对象如 card-frame, btn-primary
        if ('enabled' in value && value.enabled && value.url) {
          root.style.setProperty(`--image-${key}`, `url(${value.url})`);
          root.style.setProperty(`--image-${key}-border-size`, String(value.borderSize || 20));
        } else {
          root.style.setProperty(`--image-${key}`, 'none');
        }
      }
    });

    // 注入动画变量
    Object.entries(themeConfig.animations).forEach(([key, value]) => {
      root.style.setProperty(`--animation-${key}`, String(value));
    });

    // 注入字体变量
    Object.entries(themeConfig.typography).forEach(([key, value]) => {
      root.style.setProperty(`--typography-${key}`, String(value));
    });

    // 注入效果变量
    Object.entries(themeConfig.effects).forEach(([key, value]) => {
      root.style.setProperty(`--effect-${key}`, String(value));
    });
  }, []);

  const loadTheme = useCallback(async (themeKey: ThemeKey): Promise<Theme> => {
    switch (themeKey) {
      case 'default':
        return defaultTheme;
      default:
        return defaultTheme;
    }
  }, []);

  const setTheme = useCallback(async (themeKey: ThemeKey) => {
    try {
      const newTheme = await loadTheme(themeKey);
      setThemeState(newTheme);
      injectCSSVariables(newTheme);
      
      // 持久化用户选择
      localStorage.setItem(STORAGE_KEY, themeKey);
      
      console.log(`Theme switched to: ${themeKey}`);
    } catch (error) {
      console.error('Failed to switch theme:', error);
    }
  }, [loadTheme, injectCSSVariables]);

  // 初始化：加载用户保存的主题
  useEffect(() => {
    const initTheme = async () => {
      try {
        const savedThemeKey = localStorage.getItem(STORAGE_KEY) || 'default';
        const savedTheme = await loadTheme(savedThemeKey);
        
        setThemeState(savedTheme);
        injectCSSVariables(savedTheme);
        
        // 扫描可用的主题（未来扩展）
        const themes: ThemeKey[] = ['default'];
        setAvailableThemes(themes);
        
        // 构建主题元数据数组
        const themesMeta: ThemeMeta[] = [
          { id: 'default', name: defaultTheme.name, colors: defaultTheme.colors },
        ];
        setAvailableThemesMeta(themesMeta);
      } catch (error) {
        console.error('Failed to initialize theme:', error);
        setThemeState(defaultTheme);
        injectCSSVariables(defaultTheme);
      }
    };

    initTheme();
  }, [injectCSSVariables, loadTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, availableThemes, availableThemesMeta }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
